'use client';
import React from 'react';
import { DateTime } from 'luxon';
import { AsOf, SecuritiesManager, ValuationRatiosQuery } from '@benzinga/securities-manager';
import { SessionContext } from '@benzinga/session-context';
import Chart from './Chart';
import { ChartDataType, CompareChartProps } from './types';
import { chartColors } from './utils';

export const CompareChart: React.FC<CompareChartProps & { type: 'EPS' | 'PE' | 'DE' | 'PB' | 'ROE' }> = ({
  addSymbol,
  currentSymbol,
  removeSymbol,
  symbols,
  type,
}) => {
  const session = React.useContext(SessionContext);
  const securitiesManager = session.getManager(SecuritiesManager);

  const initialChartData = symbols.reduce((acc, symbol, index) => {
    acc[symbol] = {
      color: chartColors[index % chartColors.length],
      data: [],
      loading: true,
    };
    return acc;
  }, {} as ChartDataType);

  const [chartData, setChartData] = React.useState<ChartDataType>(initialChartData);

  const handleAddSymbol = (symbol: string) => {
    if (symbols.length < chartColors.length && !symbols.includes(symbol)) {
      addSymbol(symbol);
      const chartLineColor = chartColors[symbols.length];
      setChartData(prev => ({
        ...prev,
        [symbol]: { color: chartLineColor, data: [], loading: true },
      }));
    }
  };

  const handleRemoveSymbol = (symbol: string) => {
    removeSymbol(symbol);
    setChartData(prev => {
      delete prev[symbol];
      return prev;
    });
  };

  React.useEffect(() => {
    const fiveYearsAgo = DateTime.local().minus({ years: 5 }).toFormat('yyyy-MM-dd') as AsOf;

    async function fetchData() {
      const query = {
        asOf: fiveYearsAgo,
        period: '3M',
        symbols: symbols.join(','),
      } as ValuationRatiosQuery;

      let dataResponse;
      if (type === 'EPS') {
        dataResponse = await securitiesManager.getEarningReports(query);
      } else if (type === 'PE' || type === 'PB') {
        dataResponse = await securitiesManager.getValuationRatios(query as ValuationRatiosQuery);
      } else if (type === 'DE' || type === 'ROE') {
        dataResponse = await securitiesManager.getOperationRatios(query as ValuationRatiosQuery);
      }

      if (dataResponse.ok) {
        setChartData(prev => {
          const newChartData = { ...prev };
          Object.keys(newChartData).forEach(symbol => {
            if (newChartData[symbol].loading) {
              const index = dataResponse.ok.findIndex(item => item.symbol === symbol);
              const dataItems =
                type === 'EPS'
                  ? dataResponse.ok[index]?.earningReports
                  : type === 'PE' || type === 'PB'
                    ? dataResponse.ok[index]?.valuationRatios
                    : dataResponse.ok[index]?.operationRatios;

              const data: { date: string; value: number }[] = [];

              dataItems?.forEach(item => {
                const date =
                  type === 'EPS' ? item.id.asOf : type === 'PE' || type === 'PB' ? item.id.date : item.id.asOf;
                const value =
                  type === 'EPS'
                    ? item.basicEps
                    : type === 'PE'
                      ? item.peRatio
                      : type === 'PB'
                        ? item.pbRatio
                        : type === 'DE'
                          ? item.totalDebtEquityRatio
                          : item.roe;

                if (date && value) {
                  data.push({
                    date,
                    value,
                  });
                }
              });

              newChartData[symbol].data = data;
              newChartData[symbol].data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
              newChartData[symbol].loading = false;
            }
          });
          return newChartData;
        });
      }
    }

    fetchData();
  }, [currentSymbol, securitiesManager, symbols, type]);

  const options = React.useMemo(() => {
    return {
      series: Object.keys(chartData).map(symbol => ({
        data: chartData[symbol].data,
        lineStyle: { color: chartData[symbol].color, width: 2 },
        name: symbol,
        showSymbol: false,
        type: 'line',
        ...(symbol === currentSymbol && {
          areaStyle: {
            color: {
              colorStops: [
                { color: '#1060FF4D', offset: 0 },
                { color: '#1060FF00', offset: 1 },
              ],
              global: false,
              type: 'linear',
              x: 0,
              x2: 0,
              y: 0,
              y2: 1,
            },
          },
        }),
      })),
      tooltip: {
        axisPointer: { animation: false },
        formatter: function (params) {
          const firstParam = params[0];
          const date = new Date(firstParam.data.date);

          const bodyData: { color: string; name: string; value: string }[] = [];
          params.forEach((param: any) => {
            bodyData.push({
              color: chartData?.[param.seriesName]?.color,
              name: param.seriesName,
              value: param.data.value
                ? `${param.data.value.toFixed(2)}${['PE', 'PB', 'DE'].includes(type) ? 'x' : type === 'ROE' ? '%' : ''}`
                : 'N/A',
            });
          });

          let tooltipText = `${type} on ${DateTime.fromJSDate(date).toFormat('MM/dd/yyyy')}`;
          bodyData.forEach(data => {
            tooltipText += `<div style="color: ${data.color}">${data.name}: ${data.value}</div>`;
          });

          return tooltipText;
        },
        trigger: 'axis',
      },
      xAxis: {
        boundaryGap: false,
        data: Object.keys(chartData).reduce((acc, symbol) => {
          if (chartData[symbol].data.length > acc.length) {
            return chartData[symbol].data.map(d => d.date);
          }
          return acc || [];
        }, [] as string[]),
        type: 'category',
      },
      yAxis: { splitLine: { show: false }, type: 'value' },
    };
  }, [chartData, currentSymbol, type]);

  return (
    <Chart
      addSymbol={handleAddSymbol}
      chartData={chartData}
      currentSymbol={currentSymbol}
      options={options}
      removeSymbol={handleRemoveSymbol}
      symbols={symbols}
    />
  );
};

export const EPSChart: React.FC<CompareChartProps> = props => <CompareChart {...props} type="EPS" />;
export const PriceBookChart: React.FC<CompareChartProps> = props => <CompareChart {...props} type="PB" />;
export const PriceEarningsChart: React.FC<CompareChartProps> = props => <CompareChart {...props} type="PE" />;
export const DEChart: React.FC<CompareChartProps> = props => <CompareChart {...props} type="DE" />;
export const RoEChart: React.FC<CompareChartProps> = props => <CompareChart {...props} type="ROE" />;
